import * as XLSX from 'xlsx';

// Builds the per-file fingerprint that /upload/ingestion/detect-vendors scores. Parsing happens here,
// in the browser, so a directory drop never ships whole workbooks to the server just to guess a
// vendor — only a header row and a sample of body cells travel.

const SAMPLE_ROWS = 50;      // body rows sampled per file; matches MAX_SAMPLES_PER_COLUMN server-side
const MAX_COLUMNS = 100;     // matches MAX_COLUMNS server-side
const HEADER_SCAN_ROWS = 10; // vendor sheets often carry a title/logo preamble above the real header

// Reading a whole workbook to sample 50 rows is wasteful and can hang the tab on a huge export.
const MAX_PARSE_BYTES = 40 * 1024 * 1024; // xlsx/xls must be unzipped whole; refuse the giants
const CSV_SLICE_BYTES = 512 * 1024;       // csv is line-oriented: the first slice is enough

const TABULAR_RE = /\.(csv|xls|xlsx)$/i;
const CSV_RE = /\.csv$/i;

// react-dropzone sets `path` (with directory traversal); the folder picker sets webkitRelativePath.
// We want the immediate parent directory: "Vendors/Daltile/2026.xlsx" -> "Daltile".
export const folderNameOf = (file) => {
  const path = file.webkitRelativePath || file.path || '';
  const parts = path.split('/').filter(Boolean);
  parts.pop(); // the filename
  return parts.length ? parts[parts.length - 1] : '';
};

export const fileKeyOf = (file, index) =>
  `${file.webkitRelativePath || file.path || file.name}:${file.size}:${index}`;

// Read via FileReader rather than Blob.text()/Blob.arrayBuffer(). Those are unavailable in Safari
// before 14 (and in the jsdom build CRA tests run under), and silently falling back to "no columns"
// there would quietly disable content-based vendor detection for those users.
const readBlob = (blob, as) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error || new Error('file read failed'));
  if (as === 'text') reader.readAsText(blob);
  else reader.readAsArrayBuffer(blob);
});

const readCsvText = async (file) => {
  const slice = file.size > CSV_SLICE_BYTES ? file.slice(0, CSV_SLICE_BYTES) : file;
  const text = await readBlob(slice, 'text');
  // A truncated slice usually ends mid-row; drop the final partial line so it is not sampled.
  if (file.size > CSV_SLICE_BYTES) {
    const cut = text.lastIndexOf('\n');
    return cut > 0 ? text.slice(0, cut) : text;
  }
  return text;
};

const rowsFromWorkbook = (workbook) => {
  const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!firstSheet) return [];
  return XLSX.utils.sheet_to_json(firstSheet, { header: 1, blankrows: false, defval: '', raw: false });
};

const nonEmptyCount = (row) => row.filter((cell) => String(cell ?? '').trim()).length;

// The header is the widest of the first few rows. A title banner ("2026 PRICE LIST") occupies one
// cell; the real header occupies many, so width separates them without guessing at content.
const findHeaderIndex = (rows) => {
  let bestIndex = 0;
  let bestWidth = 0;
  for (let i = 0; i < Math.min(HEADER_SCAN_ROWS, rows.length); i += 1) {
    const width = nonEmptyCount(rows[i]);
    if (width > bestWidth) {
      bestWidth = width;
      bestIndex = i;
    }
  }
  return bestWidth >= 2 ? bestIndex : -1;
};

const toColumns = (rows) => {
  const headerIndex = findHeaderIndex(rows);
  if (headerIndex < 0) return [];

  const header = rows[headerIndex].slice(0, MAX_COLUMNS);
  const body = rows.slice(headerIndex + 1, headerIndex + 1 + SAMPLE_ROWS);

  return header
    .map((rawHeader, col) => ({
      header: String(rawHeader ?? '').trim(),
      // Sampled values are NOT deduplicated: the server weights a vendor column by how many rows
      // agree ("48 of 50 rows say Daltile"), and collapsing duplicates destroys that signal.
      samples: body.map((row) => String(row[col] ?? '').trim()).filter(Boolean),
    }))
    .filter((column) => column.header && column.samples.length);
};

/**
 * One fingerprint per file. Never throws: a file we cannot parse still contributes its filename and
 * folder name, which are real detection signals on their own.
 */
export const fingerprintFile = async (file, index) => {
  const base = {
    key: fileKeyOf(file, index),
    fileName: file.name,
    folderName: folderNameOf(file),
  };

  // PDFs and images carry no columns. The server falls back to filename/folder, and would use
  // `textSample` if we ever add client-side text extraction.
  if (!TABULAR_RE.test(file.name)) return base;
  if (file.size > MAX_PARSE_BYTES && !CSV_RE.test(file.name)) return base;

  try {
    let workbook;
    if (CSV_RE.test(file.name)) {
      workbook = XLSX.read(await readCsvText(file), { type: 'string', sheetRows: SAMPLE_ROWS + HEADER_SCAN_ROWS });
    } else {
      const buffer = await readBlob(file, 'buffer');
      workbook = XLSX.read(new Uint8Array(buffer), { type: 'array', sheetRows: SAMPLE_ROWS + HEADER_SCAN_ROWS });
    }
    return { ...base, columns: toColumns(rowsFromWorkbook(workbook)) };
  } catch {
    return base;
  }
};

// Parsing is CPU-bound on the main thread; a 200-file directory drop would jank the UI if we mapped
// them all at once. Small concurrency keeps it responsive and lets the caller show progress.
export const fingerprintAll = async (files, { concurrency = 4, onProgress } = {}) => {
  const results = new Array(files.length);
  let cursor = 0;
  let done = 0;

  const worker = async () => {
    for (let i = cursor++; i < files.length; i = cursor++) {
      results[i] = await fingerprintFile(files[i], i);
      done += 1;
      if (onProgress) onProgress(done, files.length);
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, files.length) }, worker));
  return results;
};
